/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * PageBuilderHubPage — lists pages for an application, with homepage type toggle.
 * Route: /dashboard/applications/:id/page-builder
 */

import React, { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../theme/ThemeContext';
import {
  usePages,
  useHomepageType,
  setHomepageType,
  createPage,
  getPage,
  updatePage,
  deletePage,
  publishPage,
  setPageInApp,
  setPageVisibility,
  exportPage,
  importPage,
} from '../hooks/usePages';
import { safeFileName, saveJson } from '../utils/saveFile';
import { useAlertDialog } from '../components/common/AlertDialogContext';

function StatusBadge({ status }) {
  if (status === 'published') return <span className="badge bg-success">Published</span>;
  return <span className="badge bg-warning text-dark">Draft</span>;
}

/** Whether people can reach this page at /app/<slug>. */
function InAppBadge({ inApp }) {
  if (!inApp) return null;
  return (
    <span className="badge" style={{ background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe' }}>
      <i className="bi bi-window-stack me-1" />In app
    </span>
  );
}

function VisibilityBadge({ visibility }) {
  if (visibility === 'private') {
    return (
      <span className="badge d-inline-flex align-items-center gap-1" style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', fontWeight: 600 }}>
        <i className="bi bi-lock-fill" style={{ fontSize: 10 }} /> Private
      </span>
    );
  }
  return (
    <span className="badge d-inline-flex align-items-center gap-1" style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', fontWeight: 600 }}>
      <i className="bi bi-globe" style={{ fontSize: 10 }} /> Public
    </span>
  );
}

export default function PageBuilderHubPage() {
  const { id: appId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const queryClient = useQueryClient();

  const { data: pagesData, isLoading: pagesLoading, isError: pagesError, refetch: refetchPages } = usePages(appId);
  const { data: homepageTypeData } = useHomepageType(appId);

  const allPages = pagesData?.pages || pagesData || [];
  // Components are built in the same canvas but are not pages people browse to,
  // so they get their own list rather than sitting among real pages.
  const components = allPages.filter(p => p.page_type === 'component');
  const pages = allPages.filter(p => p.page_type !== 'component');
  const currentHomepageType = homepageTypeData?.homepage_type || 'default';

  const [togglingType, setTogglingType] = useState(false);
  const [creatingPage, setCreatingPage] = useState(false);
  // 'record_template' pages are layouts for public record pages, not pages of
  // their own: they are rendered by the /p/<table>/<id> routes with a record in
  // scope, and the backend hides them from public slug resolution.
  const [newPageIsTemplate, setNewPageIsTemplate] = useState(false);
  const [creatingComponent, setCreatingComponent] = useState(false);
  const [deletingId, setDeletingId]     = useState(null);
  const [publishingId, setPublishingId] = useState(null);
  const [inAppId, setInAppId] = useState(null);
  const [settingHomeId, setSettingHomeId] = useState(null);
  const [togglingVisibilityId, setTogglingVisibilityId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPageName, setNewPageName]   = useState('');
  const [newPageVisibility, setNewPageVisibility] = useState('public');
  const [isHome, setIsHome]             = useState(false);
  const { showAlertDialog } = useAlertDialog();
  const [downloadingId, setDownloadingId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const handleToggleHomepageType = async (type) => {
    if (type === currentHomepageType || togglingType) return;
    setTogglingType(true);
    try {
      await setHomepageType(appId, type);
      queryClient.invalidateQueries({ queryKey: ['homepageType', appId] });
    } catch (err) {
      showAlertDialog({ title: 'Error', message: err.message || 'Failed to update homepage type.', type: 'error', okLabel: 'Close', showCancel: false });
    } finally {
      setTogglingType(false);
    }
  };

  const handleCreatePage = async (e) => {
    e.preventDefault();
    if (!newPageName.trim()) return;
    if (isHome && newPageVisibility === 'private') {
      showAlertDialog({
        title: 'Cannot Set as Home Page',
        message: 'This page is currently set as Private and cannot be assigned as the Home Page. To set this page as the Home Page, please change its visibility to Public and try again.',
        type: 'error',
        okLabel: 'Close',
        showCancel: false,
      });
      return;
    }
    setCreatingPage(true);
    try {
      const page = await createPage(appId, {
        name: newPageName.trim(),
        // A component is never the home page and is never served at a URL, so
        // these two carry no meaning for it.
        is_home: creatingComponent ? false : isHome,
        visibility: creatingComponent ? 'public' : newPageVisibility,
        page_type: creatingComponent
          ? 'component'
          : (newPageIsTemplate ? 'record_template' : 'standard'),
      });

      // Header and footer are deliberately NOT copied from the Home Page here.
      //
      // Copying gave every new page its own frozen duplicate, so adding one nav
      // button later meant editing every page that had ever been created — and a
      // page created before a header change kept the old one forever. Leaving
      // page_config empty lets the renderer fall back to the Home Page's live
      // header instead, so one edit reaches every page that has not deliberately
      // overridden it. A page that wants its own still can: set it in
      // Header & Footer, or point it at a Component.

      queryClient.invalidateQueries({ queryKey: ['pages', appId] });
      setShowCreateForm(false);
      setNewPageName('');
      setIsHome(false);
      setNewPageVisibility('public');
      navigate(`/dashboard/applications/${appId}/page-builder/${page.id}/edit`);
    } catch (err) {
      showAlertDialog({ title: 'Error', message: err.message || 'Failed to create page.', type: 'error', okLabel: 'Close', showCancel: false });
    } finally {
      setCreatingPage(false);
    }
  };

  const handleDelete = (page) => {
    showAlertDialog({
      title: 'Delete Page',
      message: `Are you sure you want to delete "${page.name}"? This cannot be undone.`,
      type: 'warning',
      okLabel: 'Delete',
      cancelLabel: 'Cancel',
      showCancel: true,
      onOk: async () => {
        setDeletingId(page.id);
        try {
          await deletePage(page.id);
          queryClient.invalidateQueries({ queryKey: ['pages', appId] });
        } catch (err) {
          showAlertDialog({ title: 'Error', message: err.message || 'Failed to delete page.', type: 'error', okLabel: 'Close', showCancel: false });
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const handlePickImportFile = () => {
    // Clearing first so picking the same file twice still fires onChange.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const describePlan = (plan) => {
    // Kept short on purpose: this dialog is the last thing between the admin
    // and a write, so it has to be readable at a glance rather than scrolled.
    const lines = [
      plan.slug_was_taken
        ? `"${plan.requested_slug}" is taken, so this imports as "${plan.slug}". Your existing page is untouched.`
        : `Creates "${plan.name}" at "${plan.slug}".`,
      `${plan.section_count} section(s), imported as a draft.`,
    ];

    if (plan.missing_tables?.length) {
      lines.push(
        `\nMissing here: ${plan.missing_tables.join(', ')}`,
        `Sections using them will render empty until you create them.`,
      );
    }
    if (plan.export_warnings?.length) {
      lines.push(`\n${plan.export_warnings.length} item(s) flagged when this page was exported.`);
    }
    return lines.join('\n');
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      // Nothing is written yet — this only reports what would happen, so a page
      // bound to a table this app does not have is a decision rather than a
      // silently empty section discovered later.
      const plan = await importPage(appId, file, { preview: true });

      showAlertDialog({
        title: 'Import this page?',
        message: describePlan(plan),
        type: plan.missing_tables?.length ? 'warning' : 'info',
        okLabel: 'Import',
        cancelLabel: 'Cancel',
        showCancel: true,
        onOk: async () => {
          setImporting(true);
          try {
            const result = await importPage(appId, file);
            queryClient.invalidateQueries({ queryKey: ['pages', appId] });
            showAlertDialog({
              title: 'Page imported',
              message: `"${result.page.name}" was created as a draft at "${result.page.slug}". You can open and edit it now.`,
              type: 'success',
              okLabel: 'Close',
              showCancel: false,
            });
          } catch (err) {
            showAlertDialog({ title: 'Import failed', message: err.message || 'Could not import this page. Nothing was saved.', type: 'error', okLabel: 'Close', showCancel: false });
          } finally {
            setImporting(false);
          }
        },
      });
    } catch (err) {
      showAlertDialog({ title: 'Cannot import this file', message: err.message || 'This file could not be read as a page export.', type: 'error', okLabel: 'Close', showCancel: false });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadJson = async (page) => {
    setDownloadingId(page.id);
    try {
      const doc = await exportPage(page.id);
      saveJson(doc, safeFileName(page.slug || page.name, '.page.json'));

      // The export never fails on unportable config — it records it. Surfacing
      // that here is the whole point: a silent download would hand over a file
      // with dead links in it and no way to know.
      const warnings = doc?.warnings || [];
      if (warnings.length) {
        showAlertDialog({
          title: 'Downloaded, with warnings',
          message:
            `"${page.name}" was downloaded, but ${warnings.length} item(s) could not be made ` +
            `portable and will need attention after seeding:\n\n` +
            warnings.map((w) => `• ${w.detail}`).join('\n'),
          type: 'warning',
          okLabel: 'Close',
          showCancel: false,
        });
      }
    } catch (err) {
      showAlertDialog({ title: 'Error', message: err.message || 'Failed to download page config.', type: 'error', okLabel: 'Close', showCancel: false });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteComponent = (component) => {
    showAlertDialog({
      title: 'Delete Component',
      message:
        `Delete "${component.name}"?\n\n` +
        `Any page that includes this component will stop showing it. ` +
        `Pages themselves are not deleted, and nothing else changes.`,
      type: 'warning',
      okLabel: 'Delete',
      cancelLabel: 'Cancel',
      showCancel: true,
      onOk: async () => {
        setDeletingId(component.id);
        try {
          await deletePage(component.id);
          queryClient.invalidateQueries({ queryKey: ['pages', appId] });
        } catch (err) {
          showAlertDialog({ title: 'Error', message: err.message || 'Failed to delete component.', type: 'error', okLabel: 'Close', showCancel: false });
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const handlePublish = async (page) => {
    setPublishingId(page.id);
    try {
      await publishPage(page.id);
      queryClient.invalidateQueries({ queryKey: ['pages', appId] });
    } catch (err) {
      showAlertDialog({ title: 'Error', message: err.message || 'Failed to publish page.', type: 'error', okLabel: 'Close', showCancel: false });
    } finally {
      setPublishingId(null);
    }
  };

  const handleToggleInApp = async (page) => {
    setInAppId(page.id);
    try {
      // Send the value we want, never "flip". A flip is not idempotent: React
      // StrictMode double-invokes, a double click fires twice, a retry repeats
      // — and each of those silently undoes the change while the UI, which
      // refetches once, shows the state the user asked for. Stating the target
      // makes a duplicate call a no-op.
      await setPageInApp(page.id, !page.in_app);
      queryClient.invalidateQueries({ queryKey: ['pages', appId] });
    } catch (err) {
      showAlertDialog({ title: 'Error', message: err.message || 'Could not change this page.', type: 'error', okLabel: 'Close', showCancel: false });
    } finally {
      setInAppId(null);
    }
  };

  const handleSetHome = (page) => {
    if (page.visibility === 'private') {
      showAlertDialog({
        title: 'Cannot Set as Home Page',
        message: 'This page is currently set as Private and cannot be assigned as the Home Page. To set this page as the Home Page, please change its visibility to Public and try again.',
        type: 'error',
        okLabel: 'Close',
        showCancel: false,
      });
      return;
    }
    showAlertDialog({
      title: 'Set as Home Page',
      message: `Set "${page.name}" as the Home Page? Any page currently marked as Home will be updated automatically.`,
      type: 'warning',
      okLabel: 'Set as Home',
      cancelLabel: 'Cancel',
      showCancel: true,
      onOk: async () => {
        setSettingHomeId(page.id);
        try {
          await updatePage(page.id, { is_home: true });
          queryClient.invalidateQueries({ queryKey: ['pages', appId] });
        } catch (err) {
          showAlertDialog({ title: 'Error', message: err.message || 'Failed to set home page.', type: 'error', okLabel: 'Close', showCancel: false });
        } finally {
          setSettingHomeId(null);
        }
      },
    });
  };

  const handleToggleVisibility = (page) => {
    const newVisibility = page.visibility === 'public' ? 'private' : 'public';
    const toPrivate = newVisibility === 'private';
    showAlertDialog({
      title: toPrivate ? 'Make Page Private?' : 'Make Page Public?',
      message: toPrivate
        ? `"${page.name}" will only be accessible to authenticated users.`
        : `"${page.name}" will be accessible to all visitors.`,
      type: 'warning',
      okLabel: toPrivate ? 'Make Private' : 'Make Public',
      cancelLabel: 'Cancel',
      showCancel: true,
      onOk: async () => {
        setTogglingVisibilityId(page.id);
        try {
          await setPageVisibility(page.id, newVisibility);
          queryClient.invalidateQueries({ queryKey: ['pages', appId] });
        } catch (err) {
          showAlertDialog({ title: 'Error', message: err.message || 'Failed to update visibility.', type: 'error', okLabel: 'Close', showCancel: false });
        } finally {
          setTogglingVisibilityId(null);
        }
      },
    });
  };

  return (
    <div
      className="d-flex flex-column w-100"
      style={{ background: '#f8fafc', minHeight: '100vh' }}
    >
      {/* Header */}
      <div className="w-100 px-3 py-3 px-md-4 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3 shadow-sm">
        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-light rounded-circle shadow-sm"
            onClick={() => navigate(`/dashboard/applications/${appId}`)}
            style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className="bi bi-arrow-left" />
          </button>
          <div>
            <h4 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <i className="bi bi-layout-text-window-reverse" style={{ color: theme.primary }} />
              Page Builder
            </h4>
            <small className="text-muted">Build and publish custom pages for your application.</small>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleImportFile}
            style={{ display: 'none' }}
            data-testid="page-import-input"
          />
          <button
            className="btn btn-sm"
            style={{ background: '#fff', color: '#475569', border: '1px solid #e2e8f0' }}
            onClick={handlePickImportFile}
            disabled={importing}
            title="Import a page from a downloaded JSON file"
          >
            {importing
              ? <span className="spinner-border spinner-border-sm" />
              : <><i className="bi bi-upload me-1" />Import Page</>
            }
          </button>
          <button
            className="btn btn-sm"
            style={{ background: theme.primary, color: '#fff', border: 'none' }}
            onClick={() => setShowCreateForm(true)}
          >
            <i className="bi bi-plus me-1" />
            New Page
          </button>
        </div>
      </div>

      <div className="container-fluid p-3 p-md-4" style={{ maxWidth: 1000 }}>

        {/* Homepage Type toggle */}
        <div className="bg-white rounded border shadow-sm p-4 mb-4">
          <div className="fw-bold mb-1">Homepage Type</div>
          <div className="text-muted small mb-3">Choose whether to use the platform's built-in homepage or your custom Page Builder design.</div>
          <div className="d-flex gap-3 flex-wrap">
            <label
              className="d-flex align-items-center gap-2"
              style={{ cursor: 'pointer', fontWeight: currentHomepageType === 'default' ? 700 : 400 }}
            >
              <input
                type="radio"
                name="homepageType"
                checked={currentHomepageType === 'default'}
                onChange={() => handleToggleHomepageType('default')}
                disabled={togglingType}
              />
              <div>
                <div>Default Homepage</div>
                <div className="text-muted" style={{ fontSize: 12 }}>Platform built-in design</div>
              </div>
            </label>
            <label
              className="d-flex align-items-center gap-2"
              style={{ cursor: 'pointer', fontWeight: currentHomepageType === 'custom' ? 700 : 400 }}
            >
              <input
                type="radio"
                name="homepageType"
                checked={currentHomepageType === 'custom'}
                onChange={() => handleToggleHomepageType('custom')}
                disabled={togglingType}
              />
              <div>
                <div>Custom Homepage</div>
                <div className="text-muted" style={{ fontSize: 12 }}>Use Page Builder output</div>
              </div>
            </label>
          </div>
          {togglingType && (
            <div className="mt-2 text-muted small">
              <span className="spinner-border spinner-border-sm me-1" />
              Saving…
            </div>
          )}
          {currentHomepageType === 'custom' && (
            <div className="alert alert-info mt-3 mb-0 py-2 small">
              <i className="bi bi-info-circle me-1" />
              The client will use your published <strong>Home Page</strong> from the list below.
              Make sure a page is marked as <em>Home</em> and published.
            </div>
          )}
        </div>

        {/* Components — built in the same canvas, reused across pages. */}
        <div className="d-flex align-items-center justify-content-between mb-2 mt-4">
          <h5 className="fw-bold mb-0">
            Components
            {!pagesLoading && (
              <span className="ms-2 badge bg-secondary" style={{ fontSize: 13 }}>{components.length}</span>
            )}
          </h5>
          <button
            className="btn btn-sm"
            style={{ background: '#fff', color: '#475569', border: '1px solid #e2e8f0' }}
            onClick={() => { setCreatingComponent(true); setShowCreateForm(true); }}
          >
            <i className="bi bi-plus me-1" />
            New Component
          </button>
        </div>
        <div className="text-muted small mb-3">
          Build something once — a header, a call to action — then include it on any page.
          Editing it here updates every page that uses it.
        </div>

        {!pagesLoading && components.length === 0 && (
          <div className="border rounded p-3 mb-4 text-muted small" style={{ background: '#f8fafc' }}>
            No components yet.
          </div>
        )}

        {!pagesLoading && components.length > 0 && (
          <div className="table-responsive mb-4">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Reference</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {components.map(component => (
                  <tr key={component.id}>
                    <td>{component.name}</td>
                    <td><code>{component.slug}</code></td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm"
                          style={{ background: theme.primary, color: '#fff', border: 'none', fontSize: 12 }}
                          onClick={() => navigate(`/dashboard/applications/${appId}/page-builder/${component.id}/edit`)}
                        >
                          <i className="bi bi-pencil me-1" />Edit
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', fontSize: 12 }}
                          onClick={() => handleDownloadJson(component)}
                          disabled={downloadingId === component.id}
                        >
                          {downloadingId === component.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : <><i className="bi bi-filetype-json me-1" />Download JSON</>
                          }
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ background: '#fee2e2', color: '#dc2626', border: 'none', fontSize: 12 }}
                          onClick={() => handleDeleteComponent(component)}
                          disabled={deletingId === component.id}
                        >
                          {deletingId === component.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : <i className="bi bi-trash" />
                          }
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pages list */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="fw-bold mb-0">
            Pages
            {!pagesLoading && (
              <span className="ms-2 badge bg-primary" style={{ fontSize: 13 }}>{pages.length}</span>
            )}
          </h5>
        </div>

        {pagesLoading && (
          <div className="text-center py-5">
            <span className="spinner-border" style={{ color: theme.primary }} />
          </div>
        )}

        {pagesError && (
          <div className="alert alert-danger">
            Failed to load pages.{' '}
            <button className="btn btn-sm btn-outline-danger ms-2" onClick={refetchPages}>Retry</button>
          </div>
        )}

        {!pagesLoading && !pagesError && pages.length === 0 && (
          <div className="bg-white rounded border shadow-sm p-5 text-center text-muted">
            <i className="bi bi-layout-text-window-reverse" style={{ fontSize: 48, opacity: 0.2 }} />
            <div className="fw-semibold mt-3">No pages yet</div>
            <div className="small mt-1">Click "New Page" to create your first custom page, or import one from a JSON file.</div>
            <button
              className="btn btn-sm mt-3"
              style={{ background: theme.primary, color: '#fff', border: 'none' }}
              onClick={() => setShowCreateForm(true)}
            >
              <i className="bi bi-plus me-1" />
              New Page
            </button>
          </div>
        )}

        {!pagesLoading && !pagesError && pages.length > 0 && (
          <div className="bg-white rounded border shadow-sm overflow-hidden">
            <table className="table table-hover mb-0">
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th className="small text-muted fw-semibold py-2 px-3">Name</th>
                  <th className="small text-muted fw-semibold py-2 px-3">Slug</th>
                  <th className="small text-muted fw-semibold py-2 px-3">Type</th>
                  <th className="small text-muted fw-semibold py-2 px-3">Status</th>
                  <th className="small text-muted fw-semibold py-2 px-3">Visibility</th>
                  <th className="small text-muted fw-semibold py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pages.map(page => (
                  <tr key={page.id}>
                    <td className="px-3 py-2 align-middle">
                      <div className="fw-semibold small">{page.name}</div>
                      {page.is_home && (
                        <span className="badge bg-primary ms-0 mt-1" style={{ fontSize: 10 }}>Home</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <code className="small" style={{ color: theme.primary }}>/{page.slug}</code>
                    </td>
                    <td className="px-3 py-2 align-middle small text-muted text-capitalize">
                      {page.page_type || 'standard'}
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <div className="d-flex gap-1 flex-wrap align-items-center">
                        <StatusBadge status={page.status} />
                        <InAppBadge inApp={page.in_app} />
                      </div>
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <VisibilityBadge visibility={page.visibility || 'public'} />
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <div className="d-flex gap-2 flex-wrap">
                        <button
                          className="btn btn-sm"
                          style={{ background: theme.primary, color: '#fff', border: 'none', fontSize: 12 }}
                          onClick={() => navigate(`/dashboard/applications/${appId}/page-builder/${page.id}/edit`)}
                        >
                          <i className="bi bi-pencil me-1" />
                          Edit
                        </button>
                        {page.status !== 'published' && (
                          <button
                            className="btn btn-sm btn-outline-success"
                            style={{ fontSize: 12 }}
                            onClick={() => handlePublish(page)}
                            disabled={publishingId === page.id}
                          >
                            {publishingId === page.id
                              ? <span className="spinner-border spinner-border-sm" />
                              : <><i className="bi bi-rocket-takeoff me-1" />Publish</>
                            }
                          </button>
                        )}
                        {page.status === 'published' && page.page_type !== 'record_template' && (
                          <button
                            className={`btn btn-sm ${page.in_app ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                            style={{ fontSize: 12 }}
                            onClick={() => handleToggleInApp(page)}
                            disabled={inAppId === page.id}
                            title={page.in_app
                              ? `People can open this at /app/${page.slug}. Click to take it out of the app.`
                              : `Not reachable yet. Click to make it available at /app/${page.slug}.`}
                          >
                            {inAppId === page.id
                              ? <span className="spinner-border spinner-border-sm" />
                              : page.in_app
                                ? <><i className="bi bi-dash-circle me-1" />Remove from app</>
                                : <><i className="bi bi-window-plus me-1" />Add to app</>
                            }
                          </button>
                        )}
                        {page.is_home ? (
                          <button
                            className="btn btn-sm"
                            style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', fontSize: 12, cursor: 'default' }}
                            disabled
                            title="This is currently the Home Page"
                          >
                            <i className="bi bi-house-fill me-1" />
                            Home
                          </button>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            style={{ fontSize: 12 }}
                            onClick={() => handleSetHome(page)}
                            disabled={settingHomeId === page.id}
                            title="Set as Home Page"
                          >
                            {settingHomeId === page.id
                              ? <span className="spinner-border spinner-border-sm" />
                              : <><i className="bi bi-house me-1" />Set Home</>
                            }
                          </button>
                        )}
                        <button
                          className="btn btn-sm"
                          style={{
                            background: page.visibility === 'private' ? '#f0fdf4' : '#f1f5f9',
                            color: page.visibility === 'private' ? '#16a34a' : '#475569',
                            border: `1px solid ${page.visibility === 'private' ? '#bbf7d0' : '#e2e8f0'}`,
                            fontSize: 12,
                          }}
                          onClick={() => handleToggleVisibility(page)}
                          disabled={togglingVisibilityId === page.id}
                          title={page.visibility === 'private' ? 'Make Public' : 'Make Private'}
                        >
                          {togglingVisibilityId === page.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : page.visibility === 'private'
                              ? <><i className="bi bi-globe me-1" />Make Public</>
                              : <><i className="bi bi-lock me-1" />Make Private</>
                          }
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', fontSize: 12 }}
                          onClick={() => handleDownloadJson(page)}
                          disabled={downloadingId === page.id}
                          title="Download this page's configuration as JSON"
                        >
                          {downloadingId === page.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : <><i className="bi bi-filetype-json me-1" />Download JSON</>
                          }
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ background: '#fee2e2', color: '#dc2626', border: 'none', fontSize: 12 }}
                          onClick={() => handleDelete(page)}
                          disabled={deletingId === page.id}
                        >
                          {deletingId === page.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : <i className="bi bi-trash" />
                          }
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create page modal */}
      {showCreateForm && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handleCreatePage}>
                <div className="modal-header">
                  <h5 className="modal-title fw-bold">{creatingComponent ? 'Create New Component' : 'Create New Page'}</h5>
                  <button type="button" className="btn-close" onClick={() => { setShowCreateForm(false); setNewPageName(''); setIsHome(false); setNewPageVisibility('public'); setCreatingComponent(false); }} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">{creatingComponent ? 'Component Name' : 'Page Name'}</label>
                    <input
                      className="form-control"
                      placeholder={creatingComponent ? 'e.g. Main Header, Footer CTA' : 'e.g. Home, About, Features'}
                      value={newPageName}
                      onChange={e => setNewPageName(e.target.value)}
                      autoFocus
                      required
                    />
                    <div className="form-text">
                      {creatingComponent
                        ? 'Pages will reference this component by the slug generated from its name.'
                        : 'Slug will be auto-generated from the name.'}
                    </div>
                  </div>
                  {/* None of these apply to a component: it is never the home
                      page and is never served at a URL of its own. */}
                  {!creatingComponent && (<>
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isTemplateCheck"
                      checked={newPageIsTemplate}
                      onChange={e => {
                        setNewPageIsTemplate(e.target.checked);
                        // A layout is never a page in its own right, so it can
                        // never be the home page.
                        if (e.target.checked) setIsHome(false);
                      }}
                    />
                    <label className="form-check-label" htmlFor="isTemplateCheck">
                      Record Template (layout for a public table's pages)
                    </label>
                    <div className="form-text">
                      Use <code>{'{{ table_name.field }}'}</code> in this page's HTML to place
                      record data. It is not reachable at its own URL — choose it from a public
                      table's HTML Layout section instead.
                    </div>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isHomeCheck"
                      checked={isHome}
                      disabled={newPageVisibility === 'private' || newPageIsTemplate}
                      onChange={e => setIsHome(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="isHomeCheck" style={{ color: newPageVisibility === 'private' ? '#94a3b8' : undefined }}>
                      Set as Home Page
                    </label>
                    <div className="form-text">
                      {newPageVisibility === 'private'
                        ? 'Home Page must be a Public page. Switch visibility to Public to enable this option.'
                        : 'Only one page per application can be the home page.'}
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="form-label fw-semibold mb-2">Visibility</label>
                    <div className="d-flex gap-3">
                      <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="newPageVisibility"
                          value="public"
                          checked={newPageVisibility === 'public'}
                          onChange={() => setNewPageVisibility('public')}
                        />
                        <div>
                          <div className="d-flex align-items-center gap-1">
                            <i className="bi bi-globe" style={{ color: '#16a34a', fontSize: 13 }} />
                            <span className="fw-semibold small">Public</span>
                          </div>
                          <div className="text-muted" style={{ fontSize: 11 }}>Accessible to all visitors</div>
                        </div>
                      </label>
                      <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="newPageVisibility"
                          value="private"
                          checked={newPageVisibility === 'private'}
                          onChange={() => { setNewPageVisibility('private'); setIsHome(false); }}
                        />
                        <div>
                          <div className="d-flex align-items-center gap-1">
                            <i className="bi bi-lock-fill" style={{ color: '#475569', fontSize: 13 }} />
                            <span className="fw-semibold small">Private</span>
                          </div>
                          <div className="text-muted" style={{ fontSize: 11 }}>Requires authentication</div>
                        </div>
                      </label>
                    </div>
                  </div>
                  </>)}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => { setShowCreateForm(false); setNewPageName(''); setIsHome(false); setNewPageVisibility('public'); setCreatingComponent(false); }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    style={{ background: theme.primary, color: '#fff', border: 'none' }}
                    disabled={creatingPage || !newPageName.trim()}
                  >
                    {creatingPage ? (
                      <><span className="spinner-border spinner-border-sm me-1" />Creating…</>
                    ) : (
                      <>Create &amp; Edit</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
